// Ice-Crack-only decor: flat snow track punctuated by 6 "ice tile fields".
// Each tile cracks (shakes 1.5s) the moment the local player steps on it, then
// disappears — leaving a gap the player must dodge by hopping to other tiles.
// Once cracked, the tile no longer supports the player; if no surviving tile
// is under their feet inside a field, they fall and respawn at the last gate.
//
// Solid platforms between fields carry the spinner + checkpoint gate.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";
import { sfx } from "../audio";

type TileState = "idle" | "cracked" | "falling" | "broken";

interface IceTile {
  /** Arc-length center on the path. */
  s: number;
  /** Lateral center offset. */
  l: number;
  /** Half-extents in (s, l). */
  halfDepth: number;
  halfWidth: number;
  state: TileState;
  /** Cumulative ms anyone has been standing on this tile. Once it reaches
   *  TILE_MAX_TOUCH_MS the tile falls — regardless of WHICH players did
   *  the standing. Persistent across players, so a tile that's seen
   *  1.9s of one player's weight will fall after just 0.1s from the next. */
  touchedMs: number;
  /** ms when fall animation started. */
  fallingAt: number | null;
  mesh: THREE.Mesh;
  /** Overlay mesh showing white crack lines. Added when state→cracked. */
  crackOverlay: THREE.Mesh | null;
  /** Shatter chunks spawned when the tile starts falling. */
  shatter: ShatterPiece[];
}

interface ShatterPiece {
  mesh: THREE.Mesh;
  /** World velocity in (l, y, s) — i.e. lateral, vertical, along-path. */
  vx: number;
  vy: number;
  vz: number;
  /** Spin per second around each axis. */
  rx: number;
  ry: number;
  rz: number;
}

export interface IceCrackHandle {
  group: THREE.Group;
  /** Solid platform meshes (start, between-field platforms, finish) — these
   *  always support the player. */
  supportMeshes: THREE.Object3D[];
  /** Arc-length positions of the gate platforms (where a checkpoint sits). */
  gatePositions: number[];
  /** Arc-length positions of the spinner platforms. */
  spinnerPositions: number[];
  /** Per-frame tick — call with clockMs (Date.now() − origin) and the list
   *  of all steppers currently on this client (local player + remote ghosts).
   *  Tile state machine:
   *    idle → cracked        (first stepper touches → SFX + visual cracks)
   *    cracked → falling     (different stepper touches OR same stepper sat
   *                           more than 2s since first crack)
   *    falling → broken      (1.5s after falling started; tile disappears) */
  tick: (clockMs: number, steppers: { id: string; s: number; l: number }[]) => void;
  /** Returns true if the (s,l) point is currently above an un-broken ice tile
   *  inside an ice field. clockMs is required so the brief shatter-grace window
   *  (player still supported while ice is visibly breaking) works. */
  hasIceSupport: (s: number, l: number, clockMs: number) => boolean;
}

const PLATFORM_COLOR = 0xff5fa7;
const PLATFORM_RIM = 0xffd166;
// Darker, bluer ice — reads more clearly as "frozen" against the snow theme.
const ICE_COLOR = 0x5fa9d6;
const ICE_EDGE = 0x2d6f9c;

function buildPlatform(width: number, depth: number): THREE.Group {
  const g = new THREE.Group();
  // ≥ 0.5m thick (was 0.4).
  const thickness = 0.55;
  const top = new THREE.Mesh(
    new SoftBox(width, thickness, depth),
    new THREE.MeshStandardMaterial({ color: PLATFORM_COLOR, roughness: 0.5 }),
  );
  top.position.y = 0;
  top.receiveShadow = true;
  top.castShadow = true;
  g.add(top);
  const rim = new THREE.Mesh(
    new SoftBox(width + 0.18, 0.14, depth + 0.18),
    new THREE.MeshStandardMaterial({ color: PLATFORM_RIM, roughness: 0.4 }),
  );
  rim.position.y = thickness / 2 + 0.04;
  g.add(rim);
  return g;
}

// Single shared geometry + idle material for every ice tile. Tiles vary in
// size per-field, so we scale the mesh transform instead of generating a
// fresh BoxGeometry per tile — drops hundreds of unique geometries to one.
// Once a tile starts cracking it clones the material so its per-tile
// animation (emissive, opacity) doesn't affect every other tile.
let _sharedIceGeo: THREE.BoxGeometry | null = null;
let _sharedIdleMat: THREE.MeshStandardMaterial | null = null;
function sharedIceGeometry(): THREE.BoxGeometry {
  if (_sharedIceGeo) return _sharedIceGeo;
  // Unit-ish tile (1 × 0.32 × 1) — meshes scale to their actual width/depth.
  // 0.5m minimum thickness for walkable surfaces (was 0.32 — too thin).
  _sharedIceGeo = new THREE.BoxGeometry(1, 0.55, 1);
  return _sharedIceGeo;
}
function sharedIdleMaterial(): THREE.MeshStandardMaterial {
  if (_sharedIdleMat) return _sharedIdleMat;
  _sharedIdleMat = new THREE.MeshStandardMaterial({
    color: ICE_COLOR,
    roughness: 0.32,
    metalness: 0.04,
    emissive: ICE_EDGE,
    emissiveIntensity: 0.14,
    transparent: true,
    opacity: 0.96,
  });
  return _sharedIdleMat;
}
function buildIceTile(width: number, depth: number): THREE.Mesh {
  const mesh = new THREE.Mesh(sharedIceGeometry(), sharedIdleMaterial());
  mesh.scale.set(width, 1, depth);
  mesh.receiveShadow = true;
  return mesh;
}

/** Procedural white-line crack texture, generated once and reused for every
 *  cracked tile. Drawn as a canvas with 5–6 jagged lines radiating from
 *  near-center. Used on a thin plane laid just above the tile's top face. */
let _crackTexture: THREE.CanvasTexture | null = null;
function crackTexture(): THREE.CanvasTexture {
  if (_crackTexture) return _crackTexture;
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.lineWidth = 5;
  ctx.lineCap = "round";
  const cx = size / 2 + (Math.random() - 0.5) * 20;
  const cy = size / 2 + (Math.random() - 0.5) * 20;
  for (let i = 0; i < 6; i++) {
    const ang = (i / 6) * Math.PI * 2 + Math.random() * 0.5;
    let x = cx;
    let y = cy;
    ctx.beginPath();
    ctx.moveTo(x, y);
    const segments = 4 + Math.floor(Math.random() * 3);
    let len = 35 + Math.random() * 25;
    for (let j = 0; j < segments; j++) {
      const a = ang + (Math.random() - 0.5) * 0.6;
      x += Math.cos(a) * len;
      y += Math.sin(a) * len;
      ctx.lineTo(x, y);
      len *= 0.7;
    }
    ctx.stroke();
  }
  // Small star at center
  ctx.strokeStyle = "rgba(255,255,255,1)";
  ctx.lineWidth = 7;
  ctx.beginPath();
  ctx.arc(cx, cy, 4, 0, Math.PI * 2);
  ctx.stroke();
  _crackTexture = new THREE.CanvasTexture(canvas);
  _crackTexture.colorSpace = THREE.SRGBColorSpace;
  return _crackTexture;
}

function buildCrackOverlay(width: number, depth: number): THREE.Mesh {
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(width * 0.95, depth * 0.95),
    new THREE.MeshBasicMaterial({
      map: crackTexture(),
      transparent: true,
      opacity: 0.95,
      depthWrite: false,
    }),
  );
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.y = 0.17; // just above tile top surface
  return mesh;
}

export function buildIceCrackCourse(path: Path): IceCrackHandle {
  const ic = path.iceCrack;
  if (!ic) throw new Error("buildIceCrackCourse called on non-ice-crack path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const tiles: IceTile[] = [];
  const gatePositions: number[] = [];
  const spinnerPositions: number[] = [];

  const trackWidth = ic.halfWidth * 2;

  // Start platform: from s=0 to the first ice field.
  const firstField = ic.fields[0]!;
  const startEnd = firstField.start;
  const startPlatform = buildPlatform(trackWidth, startEnd);
  startPlatform.position.set(0, 0, startEnd / 2);
  group.add(startPlatform);
  supportMeshes.push(...startPlatform.children.filter((c) => c instanceof THREE.Mesh));

  // For each field: build the tile grid AND the solid platform that follows
  // (containing the spinner and the checkpoint gate).
  const lastIdx = ic.fields.length - 1;
  for (let i = 0; i <= lastIdx; i++) {
    const field = ic.fields[i]!;
    // Tile grid: rows along s, cols along l.
    const tileDepth = 2.0;
    const tileWidth = 2.0;
    const fieldDepth = field.end - field.start;
    const rows = Math.max(6, Math.round(fieldDepth / tileDepth));
    const cols = Math.max(5, Math.round(trackWidth / tileWidth));
    const dRow = fieldDepth / rows;
    const dCol = trackWidth / cols;
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const sCenter = field.start + dRow * (r + 0.5);
        const lCenter = -ic.halfWidth + dCol * (c + 0.5);
        // Cut out alternating cells near the edges so it feels like an
        // irregular crumbling field rather than a perfect grid.
        const edge = c === 0 || c === cols - 1;
        const irregular = edge && (r % 3 === 0);
        if (irregular) continue;
        const mesh = buildIceTile(dCol * 0.96, dRow * 0.96);
        // Thicker tile geometry (0.55) — drop the centre so the top
        // surface still sits at the same Y the player used to step
        // onto. Old: thickness 0.32 → top at +0.16. New: 0.55 → set
        // y = 0.16 - 0.275 ≈ -0.115 so top stays at +0.16.
        mesh.position.set(lCenter, -0.115, sCenter);
        group.add(mesh);
        tiles.push({
          s: sCenter,
          l: lCenter,
          halfDepth: dRow / 2,
          halfWidth: dCol / 2,
          state: "idle",
          touchedMs: 0,
          fallingAt: null,
          mesh,
          crackOverlay: null,
          shatter: [],
        });
      }
    }

    // Solid platform between this field and the next (or to finish).
    const nextStart = i < lastIdx ? ic.fields[i + 1]!.start : path.length;
    const solidStart = field.end;
    const solidEnd = nextStart;
    const solidLen = solidEnd - solidStart;
    if (solidLen > 0.5) {
      const platform = buildPlatform(trackWidth, solidLen);
      platform.position.set(0, 0, solidStart + solidLen / 2);
      group.add(platform);
      supportMeshes.push(...platform.children.filter((c) => c instanceof THREE.Mesh));
      // Spinner sits near the front of the solid platform; gate near the end.
      spinnerPositions.push(solidStart + Math.min(6, solidLen * 0.35));
      gatePositions.push(solidEnd - Math.min(3, solidLen * 0.25));
    }
  }

  // Ice tiles also support the player while idle or cracked. They're added
  // to supportMeshes for the standable-surface raycaster. Falling/broken
  // tiles are hidden so the raycaster won't catch them.
  for (const t of tiles) supportMeshes.push(t.mesh);

  // Total cumulative stand-time across ALL players that any tile can
  // absorb before it cracks + falls. Persistent — a tile that's been
  // touched for 1.9s already needs only 0.1s more from the next player.
  const TILE_MAX_TOUCH_MS = 2000;
  // Visual crack threshold — once a tile has been touched this long it
  // shows the white crack overlay as a warning that it's about to give.
  const TILE_CRACK_WARN_MS = 900;
  // Total shatter animation duration (chunks falling).
  const FALL_MS = 1500;
  // Brief window after shatter starts during which the tile still counts as
  // support for the player — just long enough to register a one-frame "ice
  // broke under me" beat before dropping them.
  const SUPPORT_GRACE_MS = 20;

  function spawnShatter(t: IceTile): ShatterPiece[] {
    const pieces: ShatterPiece[] = [];
    const w = t.halfWidth * 2;
    const d = t.halfDepth * 2;
    // 2x2 grid of chunks. Each gets its own random kick + spin.
    const subW = w / 2;
    const subD = d / 2;
    for (let cx = 0; cx < 2; cx++) {
      for (let cz = 0; cz < 2; cz++) {
        const px = t.l - t.halfWidth + subW * (cx + 0.5);
        const pz = t.s - t.halfDepth + subD * (cz + 0.5);
        const mesh = new THREE.Mesh(
          new SoftBox(subW * 0.88, 0.3, subD * 0.88),
          new THREE.MeshStandardMaterial({
            color: ICE_COLOR,
            roughness: 0.28,
            metalness: 0.05,
            emissive: ICE_EDGE,
            emissiveIntensity: 0.22,
            transparent: true,
            opacity: 0.96,
          }),
        );
        mesh.position.set(px, 0, pz);
        group.add(mesh);
        // Kick chunks outward from the tile center + a downward initial velocity.
        const dirX = (cx === 0 ? -1 : 1) + (Math.random() - 0.5) * 0.4;
        const dirZ = (cz === 0 ? -1 : 1) + (Math.random() - 0.5) * 0.4;
        pieces.push({
          mesh,
          vx: dirX * (0.8 + Math.random() * 0.5),
          vy: 0.4 + Math.random() * 0.8,
          vz: dirZ * (0.8 + Math.random() * 0.5),
          rx: (Math.random() - 0.5) * 6,
          ry: (Math.random() - 0.5) * 3,
          rz: (Math.random() - 0.5) * 6,
        });
      }
    }
    return pieces;
  }

  const overlapsTile = (t: IceTile, s: number, l: number) =>
    Math.abs(s - t.s) < t.halfDepth + 0.4 && Math.abs(l - t.l) < t.halfWidth + 0.4;

  // Last clockMs for dt computation when accumulating touchedMs.
  let lastTickClockMs = -1;
  const tick = (clockMs: number, steppers: { id: string; s: number; l: number }[]) => {
    const dtMs = lastTickClockMs < 0 ? 16 : Math.max(0, Math.min(64, clockMs - lastTickClockMs));
    lastTickClockMs = clockMs;
    // Frozen Finale ships ~120 tiles — looping every one every frame
    // and inner-looping every stepper was the per-frame hot spot on
    // older iPads. Two cheap escapes:
    //
    //  1. If the tile is `idle` and there's no stepper within an
    //     8-metre s-window, skip it entirely (we couldn't possibly
    //     transition it this frame).
    //  2. If there are zero steppers, the only tiles that need work
    //     are the ones already cracked or falling. Skip the rest.
    const hasSteppers = steppers.length > 0;
    // Pre-compute the min stepper-s so we can bail out of distant tiles.
    let minStepperS = Infinity;
    let maxStepperS = -Infinity;
    for (const sp of steppers) {
      if (sp.s < minStepperS) minStepperS = sp.s;
      if (sp.s > maxStepperS) maxStepperS = sp.s;
    }
    const STEPPER_WINDOW = 8;
    for (const t of tiles) {
      if (t.state === "broken") continue;

      // Fast bail-out: an idle tile far from every stepper can't
      // change state this frame, so skip its inner loop.
      if (t.state === "idle") {
        if (!hasSteppers) continue;
        if (t.s < minStepperS - STEPPER_WINDOW) continue;
        if (t.s > maxStepperS + STEPPER_WINDOW) continue;
      }

      // Find any stepper currently on this tile.
      let activeStepper: { id: string; s: number; l: number } | null = null;
      for (const sp of steppers) {
        if (overlapsTile(t, sp.s, sp.l)) {
          activeStepper = sp;
          break;
        }
      }

      // Cumulative touch-time model. Every frame a stepper is on the
      // tile, add dt to `touchedMs`. At TILE_CRACK_WARN_MS show the
      // crack overlay (visible warning). At TILE_MAX_TOUCH_MS the tile
      // gives way — even if the player only stood 0.1s, what counts is
      // the SUM of everyone who's ever touched it.
      if (t.state === "idle" || t.state === "cracked") {
        if (activeStepper) t.touchedMs += dtMs;
        if (t.state === "idle" && t.touchedMs >= TILE_CRACK_WARN_MS) {
          t.state = "cracked";
          t.crackOverlay = buildCrackOverlay(t.halfWidth * 2, t.halfDepth * 2);
          t.crackOverlay.position.set(t.l, 0.17, t.s);
          group.add(t.crackOverlay);
          const mat = (t.mesh.material as THREE.MeshStandardMaterial).clone();
          mat.emissiveIntensity = 0.28;
          t.mesh.material = mat;
          sfx.ice(0.85);
        }
        if (t.touchedMs >= TILE_MAX_TOUCH_MS) {
          t.state = "falling";
          t.fallingAt = clockMs;
          t.mesh.visible = false;
          if (t.crackOverlay) {
            t.crackOverlay.visible = false;
            group.remove(t.crackOverlay);
            t.crackOverlay = null;
          }
          t.shatter = spawnShatter(t);
          sfx.ice(0.95);
        }
      } else if (t.state === "falling") {
        const age = clockMs - (t.fallingAt ?? clockMs);
        // Time step for chunk physics. Using a fixed-ish dt keyed off clockMs
        // keeps multiplayer clients consistent without relying on rAF dt.
        const dt = 1 / 60;
        const gravity = 18;
        for (const p of t.shatter) {
          p.vy -= gravity * dt;
          p.mesh.position.x += p.vx * dt;
          p.mesh.position.y += p.vy * dt;
          p.mesh.position.z += p.vz * dt;
          p.mesh.rotation.x += p.rx * dt;
          p.mesh.rotation.y += p.ry * dt;
          p.mesh.rotation.z += p.rz * dt;
          // Fade pieces out near the end so the disappearance reads soft.
          if (age > FALL_MS * 0.55) {
            const fadeT = (age - FALL_MS * 0.55) / (FALL_MS * 0.45);
            const mat = p.mesh.material as THREE.MeshStandardMaterial;
            mat.opacity = Math.max(0, 0.96 - fadeT);
          }
        }
        if (age >= FALL_MS) {
          t.state = "broken";
          for (const p of t.shatter) group.remove(p.mesh);
          t.shatter = [];
          t.mesh.position.y = -10;
          continue;
        }
      }
    }
  };

  const hasIceSupport = (s: number, l: number, clockMs: number): boolean => {
    for (const t of tiles) {
      if (t.state === "broken") continue;
      // Falling tiles keep supporting the player for SUPPORT_GRACE_MS so the
      // ice visibly shatters before the player drops — otherwise the fall
      // animation steals the player's "this tile broke under me" beat.
      if (t.state === "falling") {
        const age = clockMs - (t.fallingAt ?? clockMs);
        if (age > SUPPORT_GRACE_MS) continue;
      }
      if (Math.abs(s - t.s) < t.halfDepth + 0.1 && Math.abs(l - t.l) < t.halfWidth + 0.1) {
        return true;
      }
    }
    return false;
  };

  return { group, supportMeshes, gatePositions, spinnerPositions, tick, hasIceSupport };
}

/** Falling snow particle system anchored to the camera. Self-contained: call
 *  the returned tick(dt, cameraPos) each frame. */
export function buildSnowfall(): {
  group: THREE.Group;
  tick: (dt: number, cameraX: number, cameraY: number, cameraZ: number) => void;
} {
  const COUNT = 400;
  const geo = new THREE.BufferGeometry();
  const positions = new Float32Array(COUNT * 3);
  const speeds = new Float32Array(COUNT);
  const drifts = new Float32Array(COUNT);
  for (let i = 0; i < COUNT; i++) {
    positions[i * 3 + 0] = (Math.random() - 0.5) * 60;
    positions[i * 3 + 1] = Math.random() * 40 + 4;
    positions[i * 3 + 2] = (Math.random() - 0.5) * 60;
    speeds[i] = 2.0 + Math.random() * 2.5;
    drifts[i] = (Math.random() - 0.5) * 0.5;
  }
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const mat = new THREE.PointsMaterial({
    color: 0xffffff,
    size: 0.18,
    transparent: true,
    opacity: 0.85,
    depthWrite: false,
    sizeAttenuation: true,
  });
  const points = new THREE.Points(geo, mat);
  const group = new THREE.Group();
  group.add(points);
  const tick = (dt: number, camX: number, camY: number, camZ: number) => {
    const pos = geo.getAttribute("position") as THREE.BufferAttribute;
    const arr = pos.array as Float32Array;
    for (let i = 0; i < COUNT; i++) {
      const ix = i * 3;
      const iy = ix + 1;
      const iz = ix + 2;
      const px = arr[ix] ?? 0;
      let py = (arr[iy] ?? 0) - (speeds[i] ?? 2) * dt;
      const pz = arr[iz] ?? 0;
      arr[ix] = px + (drifts[i] ?? 0) * dt;
      if (py < camY - 4) {
        arr[ix] = camX + (Math.random() - 0.5) * 60;
        py = camY + 30 + Math.random() * 10;
        arr[iz] = camZ + (Math.random() - 0.5) * 60;
      }
      arr[iy] = py;
      if (Math.abs(px - camX) > 35) arr[ix] = camX + (Math.random() - 0.5) * 30;
      if (Math.abs(pz - camZ) > 35) arr[iz] = camZ + (Math.random() - 0.5) * 30;
    }
    pos.needsUpdate = true;
  };
  return { group, tick };
}
